import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosInstance';
import HasPermission from '../../utils/HasPermission';
import NationSelector from '../../components/NationSelector';
import { AdminTabs, AppButton } from '../../components/ui';
import BattleReportModal from '../../components/events/BattleReportModal';
import './css/StrikesTab.css';

const DEFAULT_SETTINGS = { defaultExpiryDays: 14, reasons: [] };

const RISK_OPTIONS = ['none', 'low', 'medium', 'high', 'critical'];
const POINT_SOURCE_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'actual_report', label: 'Exact report' },
  { value: 'replay_estimate', label: 'Replay estimate' },
  { value: 'not_available', label: 'Unavailable' },
];
const IMPACT_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'negative', label: 'Negative net' },
  { value: 'favorable', label: 'Positive net' },
  { value: 'even', label: 'Even' },
  { value: 'unresolved', label: 'Unknown impact' },
  { value: 'unscored', label: 'Unscored' },
  { value: 'same_event_nation', label: 'Same nation' },
];
const ATTACKER_NATION_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'home', label: 'Home nation attacking' },
  { value: 'opponent', label: 'Opponent nation attacking' },
];
const DEFENDER_NATION_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'home', label: 'Home nation defending' },
  { value: 'opponent', label: 'Opponent nation defending' },
];
const RULE_TYPE_OPTIONS = [
  { value: 'standard', label: 'Point / report rule' },
  { value: 'bubble_drop', label: 'Bubble drop' },
  { value: 'tier_gathering', label: 'T5+ gathering' },
];
const LOCATION_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'city', label: 'City' },
  { value: 'elite_mine', label: 'Elite mine' },
  { value: 'resource_tile', label: 'Resource tile' },
  { value: 'power_ore', label: 'Power ore mine' },
  { value: 'alliance_structure', label: 'Alliance structure' },
];

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const formatReasonType = (value) => {
  const normalized = String(value || '').trim();
  if (normalized === 'EventBattle') return 'Battle';
  if (normalized === 'AoZBattleReport') return 'Battle report';
  if (normalized === 'EventResultEntry') return 'Event result';
  return normalized || 'Manual';
};

const buildEmptyReason = () => ({
  code: `custom_${Date.now()}`,
  label: 'Custom strike reason',
  enabled: true,
  ruleType: 'standard',
  locationType: 'any',
  pointSource: 'any',
  minRiskLevel: 'high',
  minExchangeLevel: '',
  impactStatus: 'negative',
  attackerNationRole: 'any',
  defenderNationRole: 'any',
  requireNeedsReport: false,
  requireReportPresent: false,
  minDefenses: 2,
  timeframeMinutes: 10,
  minDefenderUnits: 0,
  minTroopTier: 5,
  warningLevelDelta: 1,
  reasonNoteTemplate: '',
});

const buildBubbleDropReason = () => ({
  ...buildEmptyReason(),
  code: `bubble_drop_${Date.now()}`,
  label: 'Bubble drop',
  ruleType: 'bubble_drop',
  locationType: 'city',
  minRiskLevel: 'none',
  impactStatus: 'any',
  minDefenses: 2,
  timeframeMinutes: 10,
  minDefenderUnits: 0,
  reasonNoteTemplate: 'Multiple defenses at the same city within a short window indicate a possible bubble drop.',
});

const buildTierGatheringReason = () => ({
  ...buildEmptyReason(),
  code: `t5_gathering_${Date.now()}`,
  label: 'T5+ gathering',
  ruleType: 'tier_gathering',
  locationType: 'elite_mine',
  minRiskLevel: 'none',
  impactStatus: 'any',
  minTroopTier: 5,
  reasonNoteTemplate: 'Elite mine gathering used tier 5 or higher troops.',
});

const normalizeSettingsForEdit = (settings = {}) => ({
  defaultExpiryDays: Number(settings.defaultExpiryDays || 14),
  reasons: Array.isArray(settings.reasons) ? settings.reasons.map((reason) => ({
    ...buildEmptyReason(),
    ...reason,
    enabled: reason?.enabled !== false,
    ruleType: reason?.ruleType || 'standard',
    locationType: reason?.locationType || 'any',
    minDefenses: Number(reason?.minDefenses || 2),
    timeframeMinutes: Number(reason?.timeframeMinutes || 10),
    minDefenderUnits: Number(reason?.minDefenderUnits || 0),
    minTroopTier: Number(reason?.minTroopTier || 5),
    warningLevelDelta: Number(reason?.warningLevelDelta || 1),
  })) : [],
});

export default function StrikesTab() {
  const { user } = useContext(AuthContext);
  const canWriteCommanders = HasPermission('writeCommanders');
  const [activeSubTab, setActiveSubTab] = useState('configuration');
  const [nations, setNations] = useState([]);
  const [selectedNation, setSelectedNation] = useState(null);
  const [strikeSettings, setStrikeSettings] = useState(DEFAULT_SETTINGS);
  const [strikeHistory, setStrikeHistory] = useState([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyStatus, setHistoryStatus] = useState('all');
  const [selectedReason, setSelectedReason] = useState(null);

  const selectedNationId = String(selectedNation?._id || selectedNation || '');

  useEffect(() => {
    if (!user) return;
    if (user.role === 'superUser') {
      axiosInstance.get('/nations')
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : [];
          const userNationId = typeof user?.nation === 'string' ? user.nation : user?.nation?._id;
          setNations(rows);
          setSelectedNation((prev) => prev || rows.find((nation) => nation._id === userNationId) || rows[0] || null);
        })
        .catch((error) => console.error('Error fetching nations:', error));
    } else if (typeof user.nation === 'string') {
      axiosInstance.get(`/nations/${user.nation}`)
        .then((response) => setSelectedNation(response.data))
        .catch((error) => console.error('Error fetching nation:', error));
    } else if (user.nation?._id) {
      setSelectedNation(user.nation);
    }
  }, [user]);

  useEffect(() => {
    if (!selectedNationId) return;
    setIsLoadingSettings(true);
    axiosInstance.get(`/nations/${selectedNationId}/strike-settings`)
      .then((response) => setStrikeSettings(normalizeSettingsForEdit(response?.data?.strikeSettings || DEFAULT_SETTINGS)))
      .catch((error) => console.error('Error fetching strike settings:', error))
      .finally(() => setIsLoadingSettings(false));
  }, [selectedNationId]);

  const loadStrikeHistory = useCallback(() => {
    if (!selectedNationId) return;
    setIsLoadingHistory(true);
    axiosInstance.get('/commander-strikes', {
      params: {
        nationId: selectedNationId,
        status: historyStatus,
        limit: 500,
      },
    })
      .then((response) => setStrikeHistory(Array.isArray(response?.data?.strikes) ? response.data.strikes : []))
      .catch((error) => console.error('Error fetching strike history:', error))
      .finally(() => setIsLoadingHistory(false));
  }, [historyStatus, selectedNationId]);

  useEffect(() => {
    loadStrikeHistory();
  }, [loadStrikeHistory]);

  const subTabs = useMemo(() => [
    { value: 'configuration', label: 'Strike Configuration' },
    { value: 'history', label: 'Strike History' },
  ], []);

  const updateReason = (index, patch) => {
    setStrikeSettings((prev) => ({
      ...prev,
      reasons: (prev.reasons || []).map((reason, reasonIndex) => (
        reasonIndex === index ? { ...reason, ...patch } : reason
      )),
    }));
  };

  const saveSettings = async () => {
    if (!selectedNationId) return;
    setIsSavingSettings(true);
    try {
      const response = await axiosInstance.patch(`/nations/${selectedNationId}/strike-settings`, {
        strikeSettings,
      });
      setStrikeSettings(normalizeSettingsForEdit(response?.data?.strikeSettings || DEFAULT_SETTINGS));
    } catch (error) {
      console.error('Error saving strike settings:', error);
      window.alert(error?.response?.data?.message || 'Failed to save strike settings.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  if (!user || user.role === 'user') {
    return <p>You do not have permission to manage strikes.</p>;
  }

  return (
    <div className="am-strikes-tab">
      <div className="am-strikes-header">
        <div>
          <h2>Strikes</h2>
          <p>Configure strike review rules and audit strike history for this nation.</p>
        </div>
        {user.role === 'superUser' && (
          <NationSelector nations={nations} selectedNation={selectedNation} setSelectedNation={setSelectedNation} />
        )}
      </div>

      <AdminTabs tabs={subTabs} value={activeSubTab} onChange={setActiveSubTab} className="am-strikes-subtabs" />

      {activeSubTab === 'configuration' && (
        <div className="am-strikes-panel">
          <label className="am-strikes-expiry-field">
            Default strike expiry days
            <input
              type="number"
              min="1"
              max="3650"
              value={strikeSettings.defaultExpiryDays}
              onChange={(event) => setStrikeSettings((prev) => ({ ...prev, defaultExpiryDays: event.target.value }))}
              disabled={!canWriteCommanders || isLoadingSettings}
            />
          </label>

          <div className="am-strikes-config-actions">
            <AppButton
              kind="secondary"
              onClick={() => setStrikeSettings((prev) => ({ ...prev, reasons: [...(prev.reasons || []), buildEmptyReason()] }))}
              disabled={!canWriteCommanders}
            >
              Add reason
            </AppButton>
            <AppButton
              kind="secondary"
              onClick={() => setStrikeSettings((prev) => ({ ...prev, reasons: [...(prev.reasons || []), buildBubbleDropReason()] }))}
              disabled={!canWriteCommanders}
            >
              Add bubble drop rule
            </AppButton>
            <AppButton
              kind="secondary"
              onClick={() => setStrikeSettings((prev) => ({ ...prev, reasons: [...(prev.reasons || []), buildTierGatheringReason()] }))}
              disabled={!canWriteCommanders}
            >
              Add T5+ gathering rule
            </AppButton>
            <AppButton kind="primary" onClick={saveSettings} disabled={!canWriteCommanders || isSavingSettings || !selectedNationId}>
              {isSavingSettings ? 'Saving...' : 'Save strike configuration'}
            </AppButton>
          </div>

          <div className="am-strikes-table-wrap">
            <table className="am-strikes-table">
              <thead>
                <tr>
                  <th>Enabled</th>
                  <th>Reason</th>
                  <th>Rule type</th>
                  <th>Location</th>
                  <th>Rule conditions</th>
                  <th>Point source</th>
                  <th>Risk</th>
                  <th>Exchange</th>
                  <th>Impact</th>
                  <th>Attacker nation</th>
                  <th>Defender nation</th>
                  <th>Report flags</th>
                  <th>Delta</th>
                  <th>Note template</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(strikeSettings.reasons || []).map((reason, index) => {
                  const ruleType = reason.ruleType || 'standard';
                  const isStandardRule = ruleType === 'standard';
                  const isBubbleDropRule = ruleType === 'bubble_drop';
                  const isTierGatheringRule = ruleType === 'tier_gathering';
                  const supportsNationRoleFilters = isStandardRule || isTierGatheringRule;
                  return (
                  <tr key={`${reason.code}-${index}`}>
                    <td data-label="Enabled">
                      <input
                        type="checkbox"
                        checked={reason.enabled !== false}
                        onChange={(event) => updateReason(index, { enabled: event.target.checked })}
                        disabled={!canWriteCommanders}
                      />
                    </td>
                    <td data-label="Reason">
                      <input value={reason.label || ''} onChange={(event) => updateReason(index, { label: event.target.value })} disabled={!canWriteCommanders} />
                    </td>
                    <td data-label="Rule type">
                      <select
                        value={ruleType}
                        onChange={(event) => {
                          const nextType = event.target.value;
                          const defaults = nextType === 'bubble_drop'
                            ? {
                              ruleType: nextType,
                              locationType: reason.locationType === 'any' ? 'city' : reason.locationType,
                              minRiskLevel: reason.minRiskLevel || 'none',
                              impactStatus: reason.impactStatus || 'any',
                              minDefenses: reason.minDefenses || 2,
                              timeframeMinutes: reason.timeframeMinutes || 10,
                              minDefenderUnits: reason.minDefenderUnits || 0,
                            }
                            : nextType === 'tier_gathering'
                              ? {
                                ruleType: nextType,
                                locationType: reason.locationType === 'any' ? 'elite_mine' : reason.locationType,
                                minRiskLevel: reason.minRiskLevel || 'none',
                                impactStatus: reason.impactStatus || 'any',
                                minTroopTier: reason.minTroopTier || 5,
                              }
                              : { ruleType: nextType, locationType: reason.locationType || 'any' };
                          updateReason(index, defaults);
                        }}
                        disabled={!canWriteCommanders}
                      >
                        {RULE_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Location">
                      <select value={reason.locationType || 'any'} onChange={(event) => updateReason(index, { locationType: event.target.value })} disabled={!canWriteCommanders}>
                        {LOCATION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Rule conditions">
                      {isBubbleDropRule && (
                        <div className="am-strikes-rule-fields">
                          <label>Defenses <input type="number" min="1" max="50" value={reason.minDefenses || 2} onChange={(event) => updateReason(index, { minDefenses: event.target.value })} disabled={!canWriteCommanders} /></label>
                          <label>Minutes <input type="number" min="1" max="240" value={reason.timeframeMinutes || 10} onChange={(event) => updateReason(index, { timeframeMinutes: event.target.value })} disabled={!canWriteCommanders} /></label>
                          <label>Owner units <input type="number" min="0" value={reason.minDefenderUnits || 0} onChange={(event) => updateReason(index, { minDefenderUnits: event.target.value })} disabled={!canWriteCommanders} /></label>
                        </div>
                      )}
                      {isTierGatheringRule && (
                        <div className="am-strikes-rule-fields">
                          <label>Minimum tier <input type="number" min="1" max="13" value={reason.minTroopTier || 5} onChange={(event) => updateReason(index, { minTroopTier: event.target.value })} disabled={!canWriteCommanders} /></label>
                        </div>
                      )}
                      {isStandardRule && <span className="am-strikes-muted">Uses point, risk, exchange, impact, and report fields.</span>}
                    </td>
                    <td data-label="Point source">
                      <select value={reason.pointSource || 'any'} onChange={(event) => updateReason(index, { pointSource: event.target.value })} disabled={!canWriteCommanders || !isStandardRule}>
                        {POINT_SOURCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Risk">
                      <select value={reason.minRiskLevel || 'high'} onChange={(event) => updateReason(index, { minRiskLevel: event.target.value })} disabled={!canWriteCommanders || !isStandardRule}>
                        {RISK_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </td>
                    <td data-label="Exchange">
                      <select value={reason.minExchangeLevel || ''} onChange={(event) => updateReason(index, { minExchangeLevel: event.target.value })} disabled={!canWriteCommanders || !isStandardRule}>
                        <option value="">Any</option>
                        {RISK_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </td>
                    <td data-label="Impact">
                      <select value={reason.impactStatus || 'any'} onChange={(event) => updateReason(index, { impactStatus: event.target.value })} disabled={!canWriteCommanders || !isStandardRule}>
                        {IMPACT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Attacker nation">
                      <select value={reason.attackerNationRole || 'any'} onChange={(event) => updateReason(index, { attackerNationRole: event.target.value })} disabled={!canWriteCommanders || !supportsNationRoleFilters}>
                        {ATTACKER_NATION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Defender nation">
                      <select value={reason.defenderNationRole || 'any'} onChange={(event) => updateReason(index, { defenderNationRole: event.target.value })} disabled={!canWriteCommanders || !supportsNationRoleFilters}>
                        {DEFENDER_NATION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Report flags">
                      <label><input type="checkbox" checked={reason.requireNeedsReport === true} onChange={(event) => updateReason(index, { requireNeedsReport: event.target.checked })} disabled={!canWriteCommanders || !isStandardRule} /> Needs report</label>
                      <label><input type="checkbox" checked={reason.requireReportPresent === true} onChange={(event) => updateReason(index, { requireReportPresent: event.target.checked })} disabled={!canWriteCommanders || !isStandardRule} /> Has report</label>
                    </td>
                    <td data-label="Delta">
                      <input type="number" min="1" max="10" value={reason.warningLevelDelta || 1} onChange={(event) => updateReason(index, { warningLevelDelta: event.target.value })} disabled={!canWriteCommanders} />
                    </td>
                    <td data-label="Note template">
                      <textarea value={reason.reasonNoteTemplate || ''} onChange={(event) => updateReason(index, { reasonNoteTemplate: event.target.value })} disabled={!canWriteCommanders} rows={3} />
                    </td>
                    <td data-label="Actions">
                      <AppButton
                        kind="secondary"
                        onClick={() => setStrikeSettings((prev) => ({ ...prev, reasons: prev.reasons.filter((_, reasonIndex) => reasonIndex !== index) }))}
                        disabled={!canWriteCommanders}
                      >
                        Remove
                      </AppButton>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSubTab === 'history' && (
        <div className="am-strikes-panel">
          <div className="am-strikes-history-controls">
            <label>
              Status
              <select value={historyStatus} onChange={(event) => setHistoryStatus(event.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="reversed">Reversed</option>
              </select>
            </label>
            <AppButton kind="secondary" onClick={loadStrikeHistory} disabled={isLoadingHistory}>Refresh</AppButton>
          </div>
          <div className="am-strikes-table-wrap">
            <table className="am-strikes-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Commander</th>
                  <th>Alliance</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>Delta</th>
                  <th>Reason</th>
                  <th>Reference</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {strikeHistory.map((strike) => {
                  const status = String(strike.effectiveStatus || strike.status || 'active').toLowerCase();
                  const hasReportModal = ['EventBattle', 'AoZBattleReport'].includes(String(strike.reasonModel || '')) && strike.reason;
                  return (
                    <tr key={strike._id}>
                      <td data-label="Status"><span className={`am-strikes-status status-${status}`}>{status}</span></td>
                      <td data-label="Commander">{strike.commander?.name || 'N/A'}</td>
                      <td data-label="Alliance">{strike.alliance?.acronym || strike.alliance?.name || 'N/A'}</td>
                      <td data-label="Issued">{formatDateTime(strike.createdAt)}</td>
                      <td data-label="Expires">{formatDateTime(strike.expiresAt)}</td>
                      <td data-label="Delta">+{Number(strike.warningLevelDelta || 0).toLocaleString()}</td>
                      <td data-label="Reason">
                        <strong>{strike.reasonLabel || strike.reason?.label || strike.reasonCode || 'Manual'}</strong>
                        <span>{strike.reasonNote || ''}</span>
                      </td>
                      <td data-label="Reference">
                        <strong>{formatReasonType(strike.reasonModel)}</strong>
                        <span>{strike.reason?.label || strike.reason?.reportKey || strike.reason?.battleKey || ''}</span>
                      </td>
                      <td data-label="Actions">
                        <AppButton kind="secondary" onClick={() => setSelectedReason(strike.reason)} disabled={!hasReportModal}>
                          View report
                        </AppButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!strikeHistory.length && <p>{isLoadingHistory ? 'Loading strike history...' : 'No strikes found.'}</p>}
          </div>
        </div>
      )}

      {selectedReason && (
        <BattleReportModal reason={selectedReason} title="Strike Report" onClose={() => setSelectedReason(null)} />
      )}
    </div>
  );
}
